import { useState } from "react";

import { parseAppVersion } from "../update/version";

/**
 * Always-visible build identity for dogfood deploys.
 * Pages stamps `APP_VERSION=YY.MM.BBBB+<gitsha>`; the chrome shows marketing CalVer only.
 * Click copies the full stamp (with sha) for git / Release health compare.
 */
export function formatBuildLabel(stamp: string): string {
  const trimmed = stamp.trim();
  if (trimmed.length === 0) return "version unknown";
  const { version } = parseAppVersion(trimmed);
  if (version.length === 0) return "version unknown";
  // Lock: hide sha in normal UI; full stamp stays on title + clipboard.
  return version;
}

export function BuildStamp({
  stamp = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "26.08.0001",
  surface = "console",
  compact = false,
}: {
  stamp?: string;
  surface?: string;
  /** Sidenav / dense chrome: slightly smaller. */
  compact?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const full = stamp.trim().length > 0 ? stamp.trim() : "26.08.0001";
  const label = formatBuildLabel(full);

  async function copyStamp(): Promise<void> {
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard can fail in insecure contexts / locked-down profiles; the stamp
      // remains visible to read by eye.
    }
  }

  return (
    <button
      type="button"
      className={compact ? "buildstamp buildstamp--compact" : "buildstamp"}
      onClick={() => void copyStamp()}
      title={`Build ${full} (deploy stamp). Click to copy for compare.`}
      aria-label={
        copied
          ? `Build version ${label}, copied to clipboard`
          : `Build version ${label}, click to copy full stamp`
      }
    >
      <span className="buildstamp__surface">{surface}</span>
      <span className="buildstamp__ver">{label}</span>
      {copied ? <span className="buildstamp__copied">copied</span> : null}
    </button>
  );
}
